import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../mendepartmentdetails/Sidebar';
import SubDeptImage from '../../../assets/image/department/modern-emergency-word-concept-with-flat-design_23-2147938981.jpg';

function DisasterPreparednessTraining() {
  const [Faq, setFaq] = useState(0); // first dropdown open by default
  const titleRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');

  useEffect(() => {
    if (titleRef.current) {
      const c = window.getComputedStyle(titleRef.current).color;
      setTitleColor(c);
    }
  }, []);

  // shared inline styles to keep existing CSS intact while fixing wrapping/spacing
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.35, color: '#0f172a' };
  const valText = { fontSize: '1rem', lineHeight: 1.6, color: '#334155' };
  const overviewText = { color: titleColor || '#0f172a', fontSize: '1.05rem', lineHeight: 1.6, margin: '8px 0 16px' };

  const sections = [
    {
      Que: 'Personal & Family Preparedness',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Make a kit:</span>
              <span style={valText}>
                Water (1 gal/person/day for 3 days), non-perishable food, flashlight, batteries, first-aid kit, meds, copies of IDs, cash, whistle, multi-tool, phone chargers, blankets, and supplies for pets/infants.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Plan & practice:</span>
              <span style={valText}>
                Two evacuation routes, meet-up points, out-of-area contact, and who grabs what. Run a 10-minute drill twice a year.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'Community Training & Exercises',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Workshops & drills:</span>
              <span style={valText}>
                Neighborhood tabletop exercises, shelter-in-place practice, stop-the-bleed basics, and CERT-style volunteer coordination.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Inclusive access:</span>
              <span style={valText}>
                Provide materials in multiple languages, large print, and with ASL/CC where possible; pre-identify medical, mobility, and sensory needs.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'Evacuation, Sheltering, and Alerts',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Know your zones:</span>
              <span style={valText}>
                Map flood, wildfire, and chemical-plume routes; keep paper maps in case networks are down. Identify pet-friendly shelters.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Get alerts:</span>
              <span style={valText}>
                Enable Wireless Emergency Alerts (WEA) on phones; sign up for local text/email alerting; have a NOAA weather radio as backup.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'Hazards We Plan For (Statewide)',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            'Drought, Earthquake, Extreme Temperatures, Flood, Geologic Hazards',
            'Severe Storms, Tornado, Wildfire, Communicable Diseases',
            'Dam/Levee Failure, Hazardous Materials Release, Infrastructure Incidents, Terrorism',
          ].map((line, i) => (
            <li key={`haz-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={valText}>{line}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Mitigation Basics (Why it Matters)',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Reduce future loss:</span>
              <span style={valText}>
                Targeted projects (elevations, safe rooms, hardening, drainage, defensible space) cut risk and recovery costs.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Whole Community approach:</span>
              <span style={valText}>
                Government, private sector, nonprofits, and residents all share responsibility—planning and actions are coordinated statewide.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'Funding & Grants (Examples)',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['BRIC & HMGP:', 'Pre-/post-disaster grants for resilient infrastructure and community projects.'],
            ['FMA & PA (C-G):', 'Flood mitigation assistance and post-disaster public assistance categories for permanent work.'],
            ['HHPD & PDM:', 'High-Hazard Potential Dam grants and legacy pre-disaster mitigation.'],
          ].map(([k, v], i) => (
            <li key={`fund-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Plan Cycle, Compliance, and Goals',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Standards & cycle:</span>
              <span style={valText}>
                State Hazard Mitigation Plan updated on a 5-year cycle (example cycle 2023–2028) with annual reviews; complies with DMA 2000 and 44 CFR 201.4.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Goals (high level):</span>
              <span style={valText}>
                Unite providers; improve decision-maker understanding; develop and publish data/tools; expand TA; protect critical infrastructure; support underserved communities.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'Neighborhood Risk Reduction Ideas',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Flood:', 'Clean inlets, mark low-water crossings, elevate utilities, backflow valves, flood-ready landscaping.'],
            ['Wind/Storm:', 'Shutter anchors, tree maintenance, tie-downs for outbuildings, safe-room planning.'],
            ['Wildfire:', 'Defensible space (0–5 ft clear), ember-resistant vents, clean gutters, metal mesh on openings.'],
          ].map(([k, v], i) => (
            <li key={`idea-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <section className="department-details-section">
      <div className="container">
        <div className="row">

          {/* Sidebar */}
          <div className="col-12 col-lg-4 col-xl-4">
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className="col-lg-8">
            <div className="department-details-imgbox">
              <img src={SubDeptImage} className="img-fluid" alt="Disaster Preparedness and Training" />
            </div>

            <div className="department-details-content-box">
              <h4 ref={titleRef} className="department-details-title">Disaster Preparedness and Training</h4>

              {/* Overview (always visible, same color as title but smaller) */}
              <p style={overviewText}>
                We build community resilience through training, exercises, and practical guidance—so residents, businesses,
                and local partners can prepare, respond, and recover faster with fewer losses.
              </p>

              {/* Download button BELOW overview, ABOVE other sections */}
              <div style={{ margin: '0 0 16px' }}>
                <a
                  href="/guides/GTS-State-Hazard-Mitigation-Plan-w-FEMA-Letter.pdf"
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    fontSize: '14px',
                    color: '#e75934',
                    textDecoration: 'none',
                    border: '1px solid #e75934',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'transparent',
                    width: 'fit-content'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e75934';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#e75934';
                  }}
                >
                  <i className="fa-solid fa-file-pdf" style={{ marginRight: '8px', fontSize: '14px' }} />
                  Download State Hazard Mitigation Plan (PDF)
                </a>
              </div>
            </div>

            {/* Accordion (expanded information) */}
            <div className="accordian-box">
              {sections.map((item, index) => (
                <div className={`accordian-box-item ${Faq !== index && 'active'}`} key={index}>
                  <div className="accordian-title">
                    <h5>{item.Que}</h5>
                    <i
                      className="fa-solid fa-angle-right"
                      onClick={() => setFaq(index)}
                      role="button"
                      aria-label={`Toggle ${item.Que}`}
                    />
                  </div>

                  {Faq === index && (
                    <div className="accordian-content" style={{ display: 'block' }}>
                      {item.Answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div style={{ marginTop: 12 }}>
              <Link to="/department/emergency">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: '5px' }} />
                Go Back
              </Link>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

export default DisasterPreparednessTraining;
